/**
 * ShellMain - the shell's markup: the header, the rails and their panels
 * (the attacker; the combat log with the order strip and the battlefield in
 * it; handouts and, by turns, the story, the stealth or the target), and the
 * modals.
 *
 * Every prop is read off the shell scope through `shellProps`. The column
 * slots are built by plain functions, not components, so the element tree -
 * and each panel's error boundary - is exactly what the shell used to render.
 * Extracted from DeathWatchSimulator; the markup is unchanged.
 */
import ActingLeaderBar from '../killTeam/ActingLeaderBar';
import { useLogCurtain } from '../../hooks/useLogOverlay';
import useSweepInStep from '../../hooks/useSweepInStep';
import styles from '../../styles/DeathWatchSimulator.module.css';
import AttackerPanel from '../AttackerPanel.jsx';
import CombatLogPanel from '../CombatLogPanel.jsx';
import TargetPanel from '../TargetPanel.jsx';
import ErrorBoundary from '../ErrorBoundary.jsx';
import HandoutsDrawer from '../HandoutsDrawer';
import TabContextMenu from './TabContextMenu';
import ShellColumns from './ShellColumns';
import ShellHeader from './ShellHeader';
import ShellBattlefield from './ShellBattlefield';
import PhaseTransition from '../PhaseTransition';
import AsksTray from '../AsksTray';
import ClockMantel from '../mantel/ClockMantel';
import { TestPrompts, CallTestButton } from '../story/TestCalls';
import StoryColumn from '../story/StoryColumn';
import RightFront from '../story/RightFront';
import StealthColumn from '../stealth/StealthColumn';
import StealthStrip from '../stealth/StealthStrip';
import QuietBar from '../stealth/QuietBar';
import SightingBar from '../stealth/SightingBar';
import RevealCard from '../RevealCard';
import DispositionBar from '../story/DispositionBar';
import { IntentBox } from '../story/Intents';
import { TimerBar } from '../story/DecisionTimer';
import { layoutOf } from '../../engine/phase/layout';
import { stealthOf } from '../../engine/phase/frame';
import { orderRailNode, turnCardNode } from './ShellOrderRail';
import { ShellTableModals, ShellRosterModals } from './ShellModals';
import * as shellProps from './shellProps';
import MotionStrip from '../MotionStrip';

/** The left column: the attacker panel. */
function leftColumn() {
  return (
    <ErrorBoundary label="Attacker panel">
      <AttackerPanel />
    </ErrorBoundary>
  );
}

/** The centre column: the combat log, holding the battlefield and the order rail (or the strip). */
function centreColumn(s, curtain) {
  const rail = shellProps.railArgs(s);
  const card = shellProps.turnCardArgs(s);
  return (
    <ErrorBoundary label="Combat log panel">
      <CombatLogPanel
        {...shellProps.logPanelProps(s, curtain)}
        // The rails (plan map-display E; design-gaps L2-L5): the order above the map, the log a foot ticker, the
        // story bars over the map, the bottom bar's four on the log's edge, the turn card and the stealth strip over the map.
        stealthStripNode={stealthStripNode(s)}
        battlefieldNode={
          <ShellBattlefield map={shellProps.battlefieldMap(s)} derive={shellProps.battlefieldDerive(s)} />
        }
        orderRailNode={orderRailNode(s.mapWantsColumn, rail, card.endSuggested)}
        turnCardNode={turnCardNode(s.mapWantsColumn, rail, card)}
        storyAsksNode={
          <>
            <TestPrompts asks={s.asks} testCalls={s.testCalls} roster={s.roster} />
            <RevealCard ask={s.revealAsk} />
            <DispositionBar dispositions={s.dispositions} />
            {/* The GM's one question when a kill-team stands together without its leader (Squad Mode). */}
            <ActingLeaderBar />
          </>
        }
        storyNode={
          <>
            <TimerBar timer={s.direction.frame?.timer} onClear={s.story.canDirect ? s.story.timer.clear : undefined} />
            {intentsNode(s)}
            <QuietBar quiet={s.quiet} rung={s.stealth.state.rung} />
            <SightingBar sightings={s.sightings} />
          </>
        }
        callTestNode={callTestNode(s)}
        onPinLine={s.story.canDirect ? s.story.board.pin : undefined}
      />
    </ErrorBoundary>
  );
}

/**
 * Stealth's instruments over the map in the rails (plan design-gaps, L5): the rung, the tracker's scope and, at
 * Alarm, the fight; a click opens the target panel on its Stealth tab. Only where the ladder runs.
 */
function stealthStripNode(s) {
  if (!layoutOf(s.direction.shown).wearsRung) return null;
  return (
    <StealthStrip
      stealth={s.stealth}
      fight={{ sides: s.fightSides, onStart: s.startFight }}
      onOpen={() => s.rightFront.show('stealth')}
    />
  );
}

/**
 * Intents: a player is offered the line to write one where the phase has no
 * turn order (the GM reads them in the asks tray). Outside a session there
 * is nobody to tell.
 */
function intentsNode(s) {
  if (!s.sessionId || s.isGm) return null;
  if (!layoutOf(s.direction.shown).takesIntents) return null;
  return <IntentBox asks={s.asks} name={s.actor?.name ?? s.displayName ?? ''} />;
}

/** Calling for a test is the director's: the GM, or anyone outside a session. */
function callTestNode(s) {
  if (s.sessionId && !s.isGm) return null;
  return (
    <CallTestButton
      roster={s.roster}
      party={s.fightSides.party.map((c) => c.id)}
      clocks={s.story.clocks.clocks}
      entries={s.story.board.entries}
      assistBonus={s.assistBonus}
      people={(s.voxPeople ?? []).filter((p) => !p.isGm && p.uid !== s.voxMe?.uid)}
      actedBy={s.actedBy}
      actingBy={s.actingBy}
      gmUid={s.voxMe?.uid ?? null}
      onCall={s.testCalls.callFor}
    />
  );
}

/** The right column: the handouts drawer, then the story's panels or the target's - whichever is in front. */
function rightColumn(s) {
  return (
    <>
      <ErrorBoundary label="Handouts">
        <HandoutsDrawer />
      </ErrorBoundary>
      <RightFront
        rightFront={s.rightFront}
        story={
          <ErrorBoundary label="Story">
            <StoryColumn story={s.story} people={s.voxPeople ?? []} channels={s.channelsApi?.channels ?? []} />
          </ErrorBoundary>
        }
        stealth={
          // Only where the ladder runs (the layout's word, never the phase by name).
          layoutOf(s.direction.shown).wearsRung && (
            <ErrorBoundary label="Stealth">
              <StealthColumn
                stealth={s.stealth}
                approach={s.approach}
                {...shellProps.contactsListProps(s)}
                fight={{ sides: s.fightSides, onStart: s.startFight }}
              />
            </ErrorBoundary>
          )
        }
        target={
          <ErrorBoundary label="Target panel">
            <TargetPanel {...shellProps.targetPanelProps(s)} />
          </ErrorBoundary>
        }
      />
    </>
  );
}

/**
 * @param {object} props
 * @param {object} props.shell - the shell scope, complete (see DeathWatchSimulator)
 */
export default function ShellMain({ shell: s }) {
  // The log's curtain hangs from the map's foot (plan design-gaps, L3).
  const logCurtain = useLogCurtain({ borrow: s.logBorrow });
  useSweepInStep();
  return (
    // `data-phase` is what the layout and the livery hang from. While the
    // phase is not known the columns stay MOUNTED and unseen (their refs and
    // observers attach at mount), so a reload mid-Stealth never shows Combat.
    <main className={styles.app} data-phase={s.direction.shown ?? 'unknown'} aria-busy={!s.direction.shown}>
      <div className={styles.groundBeamClip} aria-hidden="true">
        <div className={styles.groundBeam} data-testid="ground-beam" />
      </div>
      <ShellHeader {...shellProps.headerProps(s)} />
      <MotionStrip />
      <ErrorBoundary label="Clocks">
        <ClockMantel clocks={s.story.clocks} canDirect={s.story.canDirect} people={s.voxPeople ?? []} />
      </ErrorBoundary>
      <ShellColumns
        {...shellProps.columnsProps(s)}
        drumOpen={!!s.actionDrumOpen}
        handoutsOpen={!!s.handoutsContextValue?.open}
        onCloseHandouts={s.handoutsContextValue?.close}
        left={leftColumn()}
        centre={centreColumn(s, logCurtain)}
        right={rightColumn(s)}
      />

      <PhaseTransition
        phase={s.direction.shown}
        rung={layoutOf(s.direction.shown).wearsRung ? stealthOf(s.direction.frame).rung : null}
      />
      <AsksTray
        ruling={s.areaRuling}
        asks={s.asks}
        answerTest={s.answerTest.roll}
        quiet={s.quiet}
        ram={s.ramRuling}
        pinned={s.pinned}
      />
      <ShellTableModals {...shellProps.tableModalProps(s)} />
      <TabContextMenu {...shellProps.tabMenuProps(s)} />
      <ShellRosterModals {...shellProps.rosterModalProps(s)} />
    </main>
  );
}
